'use client';

import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { Children, isValidElement, useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { Text, theme } from '@frameio/vapor';

type Props = {
  highlightText: string;
  /**
   * By default this component splits the highlightText string by
   * space characters in order to pass an array of words to
   * react-highlight-word. This prop allows you to override the
   * searchWords used.
   */
  searchWords?: string[];
} & ComponentProps<typeof Text>;

/**
 * Highlight exact string matches in children text.
 */
export const HighlightedText = ({
  children,
  highlightText,
  variant = 'body',
  searchWords,
  ...rest
}: Props) => {
  const childrenAsText = useMemo(
    () =>
      Children.toArray(children)
        .flatMap((child: ReactNode) => {
          if (isValidElement(child)) {
            const element = child as ReactElement<{ children?: ReactNode }>;
            return element.props.children
              ? Children.toArray(element.props.children)
              : child;
          }
          return child;
        })
        .flat(1)
        .join(' '),
    [children]
  );

  const highlightTextWords = useMemo(() => {
    if (searchWords) return searchWords;
    return highlightText?.split(' ') ?? [];
  }, [searchWords, highlightText]);

  return (
    <Text variant={variant} {...rest}>
      <Highlighter
        autoEscape={true}
        highlightStyle={{
          backgroundColor: theme.colors.background.interactive.highlight,
          color: 'inherit',
        }}
        searchWords={highlightTextWords}
        textToHighlight={childrenAsText}
      />
    </Text>
  );
};
